import getLang from 'I18n/utils/getLang';
import { Fragment, useEffect, useState } from 'react';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Link } from 'foundations-components/transitional/components/Link';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { withErrorBoundary } from '../ErrorBoundary';
import { FeatureMonetizationTrackerProvider, useFeatureMonetizationTracker } from '../FeatureMonetizationTrackerProvider';
import { CategorizedFeatures } from './CategorizedFeatures';
import { useFocusedCategory } from './category-header/useFocusedCategory';
import { useTableHeaderConfig } from './category-header/useTableHeaderConfig';
import { useFeatureCategories, useProducts } from './data-cache/dataCacheHooks';
import { FeatureTableDataCacheProvider } from './data-cache/FeatureTableDataCache';
import { FeatureTableSessionCacheProvider } from './FeatureTableSessionCacheProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const FeatureTable = withErrorBoundary(FeatureTableWithProviders({
  isPublicUseCase: false
}), {
  componentName: 'FeatureTable'
});
export const InAppFeatureTable = withErrorBoundary(FeatureTableWithProviders({
  isPublicUseCase: false
}), {
  componentName: 'InAppFeatureTable'
});
export const PublicFeatureTable = withErrorBoundary(FeatureTableWithProviders({
  isPublicUseCase: true
}), {
  componentName: 'PublicFeatureTable'
});
function FeatureTableWithProviders({
  isPublicUseCase
}) {
  return ({
    onPurchaseMotionButtonClick,
    productApiNames,
    purchaseMotionLocation,
    revenueAttributionUniqueId,
    screen,
    skuCategoryName,
    usageTracker,
    StyledWrapper,
    term
  }) => {
    return /*#__PURE__*/_jsx(FeatureTableSessionCacheProvider, {
      onPurchaseMotionButtonClick: onPurchaseMotionButtonClick,
      productApiNames: productApiNames,
      purchaseMotionLocation: purchaseMotionLocation || 'MERCH_CARD',
      revenueAttributionUniqueId: revenueAttributionUniqueId,
      screen: screen,
      skuCategoryName: skuCategoryName,
      term: term,
      children: /*#__PURE__*/_jsx(FeatureTableDataCacheProvider, {
        isPublicUseCase: isPublicUseCase,
        children: /*#__PURE__*/_jsx(FeatureMonetizationTrackerProvider, {
          tracker: usageTracker,
          children: /*#__PURE__*/_jsx(FeatureTableComponent, {
            skuCategoryName: skuCategoryName,
            StyledWrapper: StyledWrapper
          })
        })
      })
    });
  };
}
function FeatureTableComponent({
  skuCategoryName,
  StyledWrapper = Fragment
}) {
  const tracker = useFeatureMonetizationTracker();
  const [visibleCategories, setVisibleCategories] = useState([]);
  const featureCategories = useFeatureCategories() || [];
  useEffect(() => {
    if (skuCategoryName) {
      setVisibleCategories([]);
    }
  }, [skuCategoryName]);
  const focusedCategory = useFocusedCategory({
    allCategories: featureCategories.map(category => category.category.categoryName),
    visibleCategories
  });
  const products = useProducts();
  const tableHeaderConfig = useTableHeaderConfig();
  if (!products || products.length === 0 || featureCategories.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(StyledWrapper, {
    children: /*#__PURE__*/_jsxs(View, {
      backgroundColor: "fill-surface-default",
      inlineSize: "100%",
      children: [featureCategories.map(featureCategory => /*#__PURE__*/_jsx(CategorizedFeatures, {
        category: featureCategory,
        focusedCategory: focusedCategory,
        tableHeaderConfig: tableHeaderConfig,
        onIntersectionChange: isIntersecting => {
          const categoryName = featureCategory.category.categoryName;
          if (isIntersecting) {
            setVisibleCategories(prevState => [...prevState, categoryName]);
          } else {
            setVisibleCategories(prevState => prevState.filter(category => category !== categoryName));
          }
        }
      }, featureCategory.category.id)), /*#__PURE__*/_jsx(View, {
        minBlockSize: "48px",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          marginInlineStart: "500"
          // Maintain alignment with the parent feature
          ,
          paddingBlockStart: "100",
          paddingBlockEnd: "200",
          textAlign: "right",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "feature-monetization.productServicesCatalogPrompt.prompt",
            options: {
              link: /*#__PURE__*/_jsx(Link, {
                href: getProductServicesCatalogHref(),
                external: true,
                onClick: () => {
                  tracker.track({
                    action: 'clicked-product-services-catalog-link',
                    skuCategoryName,
                    object: 'product-services-catalog-link'
                  });
                },
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "feature-monetization.productServicesCatalogPrompt.linkText"
                })
              })
            }
          })
        })
      })]
    })
  });
}
function getProductServicesCatalogHref() {
  const language = getLang() || 'en';
  let productServicesCatalogLanguage = language;
  const localizedLanguages = ['ja', 'es', 'fr', 'de', 'pt'];
  const base = 'https://legal.hubspot.com';
  if (language === 'ja') {
    productServicesCatalogLanguage = 'jp';
  } else if (language === 'pt') {
    productServicesCatalogLanguage = 'br';
  }
  return localizedLanguages.includes(language) ? `${base}/${productServicesCatalogLanguage}/hubspot-product-and-services-catalog` : `${base}/hubspot-product-and-services-catalog`;
}